/**
 * The text tool and the sticky note tool, from the tap to the words on the board
 * (SRS C4).
 *
 * A tap makes an empty box and opens it for typing. A tap on a box that is already
 * there opens that one instead, so a teacher who wants to correct a word does not
 * have to make a new box on top of the old one.
 *
 * The typing itself happens in a real text field over the canvas, not here: a caret,
 * selection, a phone's own keyboard and every language that needs one all come free
 * that way, and none of them can be reproduced by drawing letters on a canvas. This
 * file owns what that field is editing and what happens when it closes.
 *
 * An empty box is never shared and never saved. A teacher who taps by accident and
 * taps away again leaves nothing behind.
 */

import type { CanvasLayerName } from '@opal/contracts';
import type { BoardObject, Layer, Point, StickyColor, TextAlign, Unsubscribe } from '@opal/shared';

import { isEmptyEdit, NO_EDIT, type BoardEdit } from './board-edit.js';
import type { ObjectBoard } from './object-board.js';
import { boxContains } from './object-view.js';
import { buildSticky, buildText, contentOf, fittedToLines, isTextual, withData } from './text-object.js';
import { linesOf } from './text-paint.js';

/** How the text tool is set when a new box is made. */
export interface TextStyle {
  readonly color: string;
  readonly fontSize: number;
  readonly bold: boolean;
  readonly italic: boolean;
  readonly align: TextAlign;
}

export interface TextSessionOptions {
  /** Where a finished box lands. */
  readonly objects: ObjectBoard;
  readonly userId: string;
  /** Which of the two tools is in hand. Read at the moment of the tap. */
  readonly kind: () => 'text' | 'sticky';
  /** How the text tool is set. Read at the same moment, for the same reason. */
  readonly style: () => TextStyle;
  /** Which paper a new sticky note is on. */
  readonly paper: () => StickyColor;
  /** SRS C8 — which layer a new box goes on. Read the moment the board is tapped. */
  readonly layer?: () => Layer;
  readonly onChanged: (layer: CanvasLayerName) => void;
  /** Tells the screen to open, move or close the text field over the canvas. */
  readonly onEditing: (object: BoardObject | null) => void;
}

/**
 * A change to how the box being typed into looks, made while it is open.
 *
 * Only the fields being changed are given. Every other part of the box is left as it
 * was, so making words bold does not also reset their size.
 */
export interface TextRestyle {
  /** A text box: the ink colour. A sticky note: the name of the paper. */
  readonly color?: string;
  readonly fontSize?: number;
  readonly fontWeight?: number;
  readonly italic?: boolean;
  readonly align?: TextAlign;
}

/** What the text field over the canvas can do to the box it is editing. */
export interface TextActions {
  /** The words as they stand. Nothing is shared until the field closes. */
  update(content: string): void;
  /** Bold, bigger, centred: the box being typed into changes as the panel is used. */
  restyle(patch: TextRestyle): void;
  /** The field closed: share the words, or drop the box if it is still empty. */
  commit(): void;
}

export interface TextSession extends TextActions {
  /** A tap: opens the box under it, or makes a new one there. */
  place(at: Point): void;
  /** The gesture was taken away: a box that was never typed into goes. */
  cancel(): void;
  /** The box being typed into, or null. */
  readonly editing: BoardObject | null;
  /** Called with each finished change, for sharing and saving. */
  onEdited(handler: (edit: BoardEdit) => void): Unsubscribe;
}

interface State {
  readonly options: TextSessionOptions;
  readonly handlers: Set<(edit: BoardEdit) => void>;
  readonly layer: () => Layer;
  editing: BoardObject | null;
  /** True while the box has never been on anybody else's board. */
  fresh: boolean;
  /** The box as it was when the field opened, to go back to if the gesture is lost. */
  before: BoardObject | null;
}

function publish(state: State, edit: BoardEdit): void {
  if (isEmptyEdit(edit)) return;
  for (const handler of state.handlers) handler(edit);
}

/** The topmost text box or sticky note under a point, if there is one. */
function boxAt(state: State, at: Point): BoardObject | null {
  const nearby = state.options.objects.near({ x: at.x, y: at.y, width: 0, height: 0 });
  for (let index = nearby.length - 1; index >= 0; index -= 1) {
    const object = nearby[index];
    if (object && isTextual(object) && boxContains(object, at)) return object;
  }
  return null;
}

/** A new, empty box of whichever kind is in hand. */
function make(state: State, at: Point): BoardObject {
  const options = state.options;
  if (options.kind() === 'sticky') {
    return buildSticky({
      at,
      userId: options.userId,
      color: options.paper(),
      layer: state.layer(),
    });
  }
  const style = options.style();
  return buildText({
    at,
    userId: options.userId,
    color: style.color,
    fontSize: style.fontSize,
    bold: style.bold,
    italic: style.italic,
    align: style.align,
    layer: state.layer(),
  });
}

/** Opens a box for typing: on the board, hidden from the painter, field open. */
function open(state: State, object: BoardObject, fresh: boolean): void {
  state.editing = object;
  state.fresh = fresh;
  state.before = fresh ? null : object;
  state.options.objects.put(object);
  state.options.objects.hide(object.id);
  state.options.onEditing(object);
  state.options.onChanged('static');
}

/** Closes the field, whatever the reason, and lets the painter have the box back. */
function close(state: State): BoardObject | null {
  const object = state.editing;
  state.editing = null;
  state.before = null;
  state.options.objects.hide(null);
  state.options.onEditing(null);
  state.options.onChanged('static');
  return object;
}

function finish(state: State): void {
  const fresh = state.fresh;
  const object = close(state);
  if (!object) return;

  // An empty box is not work: it is a tap in the wrong place. It leaves nothing.
  if (contentOf(object).trim() === '') {
    state.options.objects.remove(object.id);
    state.options.onChanged('static');
    publish(state, fresh ? NO_EDIT : { ...NO_EDIT, removed: [object.id] });
    return;
  }

  publish(
    state,
    fresh ? { ...NO_EDIT, objectsAdded: [object] } : { ...NO_EDIT, objectsChanged: [object] },
  );
}

/** A tap: the box under it is opened, or a new one is made there. */
function place(state: State, at: Point): void {
  // Tapping while a box is open finishes that one first: two boxes can never be open
  // at once, so what is typed always has one place to go.
  if (state.editing) finish(state);
  const existing = boxAt(state, at);
  open(state, existing ?? make(state, at), existing === null);
}

/** Everything the field over the canvas can change about the box it is holding. */
interface TextPatch extends TextRestyle {
  readonly content?: string;
}

/** The parts of a patch that were actually given, so nothing is blanked by accident. */
function given(patch: TextPatch): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(patch)) {
    if (value !== undefined) out[key] = value;
  }
  return out;
}

/** The box being typed into, changed. Nothing is shared until the field closes. */
function amend(state: State, patch: TextPatch): void {
  const object = state.editing;
  if (!object) return;
  const written = withData(object, given(patch));
  // The box grows and shrinks to hold what is in it, so what the eraser and the
  // select tool find is always the box the teacher can see.
  state.editing = fittedToLines(written, linesOf(written).length);
  state.options.objects.put(state.editing);
  // The field over the canvas is the box: it has to grow with it, so the screen is
  // told about every change, not only about the box opening and closing.
  state.options.onEditing(state.editing);
  state.options.onChanged('static');
}

/**
 * The gesture was taken away.
 *
 * A box that was never on anybody else's board goes with the gesture that made it.
 * One that was already there goes back to exactly what it said before, because
 * nothing typed into it was ever shared.
 */
function abandon(state: State): void {
  const before = state.before;
  const fresh = state.fresh;
  const object = close(state);
  if (!object) return;
  if (fresh) state.options.objects.remove(object.id);
  else if (before) state.options.objects.put(before);
  state.options.onChanged('static');
}

export function createTextSession(options: TextSessionOptions): TextSession {
  const state: State = {
    options,
    handlers: new Set<(edit: BoardEdit) => void>(),
    layer: options.layer ?? (() => 'content'),
    editing: null,
    fresh: false,
    before: null,
  };

  return {
    get editing() {
      return state.editing;
    },

    place(at) {
      place(state, at);
    },

    update(content) {
      amend(state, { content });
    },

    restyle(patch) {
      amend(state, patch);
    },

    commit() {
      finish(state);
    },

    cancel() {
      abandon(state);
    },

    onEdited(handler) {
      state.handlers.add(handler);
      return () => {
        state.handlers.delete(handler);
      };
    },
  };
}
